import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  ReactNode,
  useState,
  useMemo,
} from "react";
import { useAppointmentsData } from "../hooks/useAppointmentsData";
import { useAppSelector } from "../../../services/hooks";
import { adminSelector } from "../../../services/selectors/user.selectors";
import {
  useLazyGetTherapistsByCompanyQuery,
  useLazyGetTherapistAvailabilityQuery,
  useLazyGetAllTherapistsAvailabilityQuery,
} from "../../../services/api/user.api";
import { useLazyGetAllCompaniesByCompanyIdQuery } from "../../../services/api/dashboards.api";
import { useGetCompanyFundingSourcesQuery } from "../../../services/api/utils.api";
import { TTherapistAvailability } from "../../../services/types/user.types";
import {
  IAppointment,
  ITherapist,
  ICompany,
  IFundingSource,
  IAppointmentFilters,
} from "../types/appointments.types";
import {
  getStoredCalendarState,
  setStoredCalendarState,
} from "../utils/calendarStorage";
import i18n from "../../../i18n";

interface AppointmentsContextValue {
  allAppointments: IAppointment[];
  isLoading: boolean;
  loadedMonths: string[];
  therapistsList: ITherapist[];
  therapistAvailability: TTherapistAvailability[];
  allTherapistsAvailability: TTherapistAvailability[];
  companiesOptions: ICompany[];
  fundingSourcesList: IFundingSource[];
  filters: IAppointmentFilters;
  setFilters: (filters: IAppointmentFilters) => void;
  ensureMonthLoaded: (date: Date) => Promise<void>;
  ensureDateRangeLoaded: (start: Date, end: Date) => Promise<void>;
  reloadMonth: (date: Date) => Promise<void>;
  filterByDateRange: (start: Date, end: Date) => IAppointment[];
  filterByTherapist: (therapistId?: number) => IAppointment[];
  filterByPatientSearch: (searchTerm?: string) => IAppointment[];
  filterAppointments: (options: {
    dateRange?: { start: Date; end: Date };
    fakeCompanyIds?: number[];
    therapistId?: number;
    therapistIds?: number[];
    fundingSourceIds?: number[];
    patientSearch?: string;
    includeOtherEventsForAdminId?: number;
  }) => IAppointment[];
  isFullscreen: boolean;
  toggleFullscreen: () => void;
  showFiltersInFullscreen: boolean;
  setShowFiltersInFullscreen: React.Dispatch<React.SetStateAction<boolean>>;
}

const AppointmentsContext = createContext<AppointmentsContextValue | undefined>(
  undefined,
);

interface AppointmentsProviderProps {
  children: ReactNode;
}

/**
 * Appointments Context Provider
 *
 * Provides shared appointments data and caching across all view components (Daily, Weekly, Monthly).
 * This ensures that data is only fetched once per month and shared across all views,
 * preventing duplicate API calls when switching between views.
 * Also provides therapists list and companies options.
 */
export const AppointmentsProvider: React.FC<AppointmentsProviderProps> = ({
  children,
}) => {
  const admin = useAppSelector(adminSelector);
  const [getTherapistsByCompany] = useLazyGetTherapistsByCompanyQuery();
  const [getAllCompaniesList] = useLazyGetAllCompaniesByCompanyIdQuery();
  const [getTherapistAvailability] = useLazyGetTherapistAvailabilityQuery();
  const [getAllTherapistsAvailability] =
    useLazyGetAllTherapistsAvailabilityQuery();

  const [therapistsList, setTherapistsList] = useState<ITherapist[]>([]);
  const [therapistAvailability, setTherapistAvailability] = useState<
    TTherapistAvailability[]
  >([]);
  const [allTherapistsAvailability, setAllTherapistsAvailability] = useState<
    TTherapistAvailability[]
  >([]);
  const [companiesOptions, setCompaniesOptions] = useState<ICompany[]>([]);
  const [filters, setFiltersState] = useState<IAppointmentFilters>(() => {
    const stored = getStoredCalendarState();
    return stored?.filters ?? {};
  });
  const [isFullscreen, setIsFullscreen] = useState(false);
  const toggleFullscreen = useCallback(() => setIsFullscreen((v) => !v), []);
  const [showFiltersInFullscreen, setShowFiltersInFullscreen] = useState(false);

  const setFilters = (newFilters: IAppointmentFilters) => {
    setFiltersState(newFilters);
    setStoredCalendarState({ filters: newFilters });
  };

  // Load funding sources for the company
  const { data: fundingSourcesData } = useGetCompanyFundingSourcesQuery(
    { companyId: admin.companyId },
    { skip: !admin.companyId },
  );

  // Map ALL funding sources (not just company-specific) for filtering
  const fundingSourcesList: IFundingSource[] =
    fundingSourcesData?.FundingSourcesList?.map((item: any) => ({
      id: item.id,
      name: item.name || "",
    })) || [];

  const appointmentsData = useAppointmentsData({
    companyId: admin.companyId,
    fakeCompanyId: admin.fakeCompanyId || undefined,
  });

  // Load companies list
  useEffect(() => {
    const loadCompaniesList = async () => {
      const result = await getAllCompaniesList();
      if (result.data) {
        // Filter out the parent company (Id=1) and "All" option (Id=-1), keep only sub-companies
        const subCompanies = result.data.filter(
          (c: any) => c.Id !== 1 && c.Id !== -1,
        );
        setCompaniesOptions(
          subCompanies.map((c: any) => ({
            id: c.Id,
            name: i18n.language === "he" ? c.Name : c.NameEn,
          })),
        );
      }
    };

    loadCompaniesList();
  }, [getAllCompaniesList]);

  // Load therapists list
  useEffect(() => {
    const loadTherapistsList = async () => {
      if (admin.companyId) {
        const result = await getTherapistsByCompany();
        if (result.data) {
          setTherapistsList(
            result.data.map((therapist: any) => ({
              id: therapist.id ?? therapist.Id,
              guid: therapist.guid ?? therapist.Guid,
              name: therapist.fullName ?? therapist.FullName,
              isActive: (therapist.statusId ?? therapist.StatusId) === 1,
              fakeCompanyId:
                therapist.fakeCompanyId ?? therapist.FakeCompanyId ?? null,
              hasDevices: therapist.hasDevices ?? therapist.HasDevices ?? false,
              deviceCount: therapist.deviceCount ?? therapist.DeviceCount ?? 0,
            })),
          );
        }
      }
    };

    loadTherapistsList();
  }, [admin.companyId, admin.fakeCompanyId, getTherapistsByCompany]);

  // Fetch ALL therapists availability at once from the server
  useEffect(() => {
    const fetchAllAvailability = async () => {
      if (!admin.companyId) {
        setAllTherapistsAvailability([]);
        return;
      }

      try {
        const result = await getAllTherapistsAvailability();
        setAllTherapistsAvailability(result.data || []);
      } catch (error) {
        console.error("Failed to fetch all therapists availability:", error);
      }
    };

    fetchAllAvailability();
  }, [admin.companyId, getAllTherapistsAvailability]);

  // Keep `therapistAvailability` (filtered for calendar views) synced locally
  useEffect(() => {
    if (!filters.therapistIds || filters.therapistIds.length === 0) {
      setTherapistAvailability([]);
      return;
    }

    setTherapistAvailability(
      allTherapistsAvailability.filter((a) => {
        const tId = a.therapistsId ?? (a as any).TherapistsId;
        return filters.therapistIds!.includes(tId);
      }),
    );
  }, [filters.therapistIds, allTherapistsAvailability]);

  // Filter therapists list by fakeCompanyId when fakeCompanyIds filter is applied
  const filteredTherapistsListByCompany = useMemo(() => {
    if (!filters.fakeCompanyIds || filters.fakeCompanyIds.length === 0) {
      return therapistsList;
    }
    return therapistsList.filter(
      (t) =>
        t.fakeCompanyId && filters.fakeCompanyIds!.includes(t.fakeCompanyId),
    );
  }, [therapistsList, filters.fakeCompanyIds]);

  // Load current month on mount
  useEffect(() => {
    if (admin.companyId) {
      const today = new Date();
      appointmentsData.ensureMonthLoaded(today);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [admin.companyId]); // Only run when companyId changes

  return (
    <AppointmentsContext.Provider
      value={{
        ...appointmentsData,
        therapistsList: filteredTherapistsListByCompany,
        therapistAvailability,
        allTherapistsAvailability,
        companiesOptions,
        fundingSourcesList,
        filters,
        setFilters,
        isFullscreen,
        toggleFullscreen,
        showFiltersInFullscreen,
        setShowFiltersInFullscreen,
      }}
    >
      {children}
    </AppointmentsContext.Provider>
  );
};

/**
 * Hook to access appointments context
 * Must be used within AppointmentsProvider
 */
export const useAppointmentsContext = (): AppointmentsContextValue => {
  const context = useContext(AppointmentsContext);
  if (!context) {
    throw new Error(
      "useAppointmentsContext must be used within AppointmentsProvider",
    );
  }
  return context;
};
